import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPostAuthRedirectPath } from '../postAuthRouting';
import { useMessageToast } from '../../shared/hooks/useMessageToast';
import { LanguageToggle } from '../../locale/components/LanguageToggle';
import layoutStyles from '../components/AuthLayout.module.css';
import primitives from '../../../styles/primitives.module.css';

export const RenewPasswordPage = ({
  renewPassword,
  onLogout,
  isChecking,
  t,
  locale,
  onLocaleChange,
}) => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({ newPassword: '', confirmPassword: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormState((prevState) => ({ ...prevState, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setErrorMessage('');
      setNoticeMessage('');

      const newPassword = formState.newPassword;
      const confirmPassword = formState.confirmPassword;

      if (!newPassword || !confirmPassword) {
        setErrorMessage(t('renewPasswordMissing'));
        return;
      }

      if (newPassword !== confirmPassword) {
        setErrorMessage(t('renewPasswordMismatch'));
        return;
      }

      setIsSubmitting(true);

      try {
        const response = await renewPassword(newPassword, confirmPassword);
        setFormState({ newPassword: '', confirmPassword: '' });
        setNoticeMessage(t('renewPasswordSuccess'));
        navigate(
          getPostAuthRedirectPath({
            requiresPasswordRenewal: false,
            branchesInitialized: Boolean(response?.branches_initialized),
            needsBranchAccessWaiting:
              Boolean(response?.branches_initialized) &&
              (response?.accessible_branch_count ?? 0) === 0,
            needsBranchSelection: Boolean(response?.needs_branch_selection),
          }),
          { replace: true },
        );
      } catch (error) {
        if (error?.status === 400) {
          setErrorMessage(t('renewPasswordInvalid'));
        } else {
          setErrorMessage(t('renewPasswordFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [formState.confirmPassword, formState.newPassword, navigate, renewPassword, t],
  );

  const handleLogout = useCallback(async () => {
    setErrorMessage('');
    setNoticeMessage('');
    try {
      await onLogout();
    } catch {
      setErrorMessage(t('renewPasswordFailed'));
    }
  }, [onLogout, t]);

  useMessageToast(noticeMessage, 'success');
  useMessageToast(errorMessage, 'error');

  return (
    <div className={layoutStyles.shell}>
      <main className={layoutStyles.panel}>
        <header className={layoutStyles.header}>
          <div className={layoutStyles.intro}>
            <p className={primitives.eyebrow}>{t('appName')}</p>
            <h1>{t('renewPasswordTitle')}</h1>
            <p className={primitives.subtitle}>{t('renewPasswordSubtitle')}</p>
          </div>
        </header>

        {!isChecking ? (
          <form className={primitives.form} onSubmit={handleSubmit}>
            <label className={primitives.field}>
              <span>{t('newPasswordLabel')}</span>
              <input
                name="newPassword"
                type="password"
                autoComplete="new-password"
                value={formState.newPassword}
                onChange={handleInputChange}
                placeholder={t('newPasswordPlaceholder')}
                required
              />
            </label>
            <label className={primitives.field}>
              <span>{t('confirmPasswordLabel')}</span>
              <input
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={formState.confirmPassword}
                onChange={handleInputChange}
                placeholder={t('confirmPasswordPlaceholder')}
                required
              />
            </label>
            <button className={primitives.primaryButton} type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('renewingPassword') : t('renewPasswordSubmit')}
            </button>
            <button
              className={primitives.ghostButton}
              type="button"
              disabled={isSubmitting}
              onClick={handleLogout}
            >
              {t('logout')}
            </button>
          </form>
        ) : null}

        <div className={layoutStyles.footer}>
          <LanguageToggle locale={locale} onChange={onLocaleChange} />
        </div>
      </main>
    </div>
  );
};
